import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Sparkles, Wrench, Bed, Coffee, HelpCircle } from "lucide-react";
import GuestLayout from "@/components/GuestLayout";
import { createRequest, getSettings, generateWhatsAppLink, getGuestOTPStatus, sendGuestOTP, verifyGuestOTP } from "@/lib/api";
import { getPropertyId, getPropertySlug, getGuestInfo, setGuestInfo } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Requests() {
  const navigate = useNavigate();
  const propertyId = getPropertyId();
  const [settings, setSettings] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [calling, setCalling] = useState(false);

  const savedInfo = getGuestInfo();
  const [formData, setFormData] = useState({
    guest_name: savedInfo.name || "",
    room_number: savedInfo.room || "",
    whatsapp: savedInfo.whatsapp || "",
    message: "",
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings(propertyId);
        setSettings(data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, [propertyId]);

  const categories = [
    { id: "room_cleaning", label: "Room Cleaning", icon: Sparkles, color: "bg-[#2A9D8F]" },
    { id: "washroom_cleaning", label: "Washroom Cleaning", icon: Sparkles, color: "bg-[#264653]" },
    { id: "maintenance", label: "Maintenance", icon: Wrench, color: "bg-[#FFC107]" },
    { id: "amenities", label: "Amenities", icon: Coffee, color: "bg-[#2A9D8F]" },
    { id: "room_service", label: "Room Service", icon: Bed, color: "bg-[#FFC107]" },
    { id: "other", label: "Other", icon: HelpCircle, color: "bg-[#6B705C]" },
  ];

  const finalizeSubmit = async () => {
    // Create request in backend
    await createRequest({
      guest_name: formData.guest_name,
      room_number: formData.room_number,
      whatsapp: formData.whatsapp,
      category: selectedCategory,
      message: formData.message,
      property_id: propertyId,
    });

    // Generate WhatsApp message
    const categoryLabel = categories.find((c) => c.id === selectedCategory)?.label || selectedCategory;
    const message = `📋 *New Guest Request*\n\n` +
      `*Category:* ${categoryLabel}\n` +
      `*Guest:* ${formData.guest_name}\n` +
      `*Room:* ${formData.room_number}\n` +
      `*WhatsApp:* ${formData.whatsapp}\n\n` +
      `*Request:*\n${formData.message}\n` +
      `\n_Sent via Hidden Monkey Stays_`;

    // Open WhatsApp - Sent to Manager (Staff 2)
    const managerNum = settings?.staff2_whatsapp || (Array.isArray(settings?.manager_numbers) ? settings.manager_numbers[0] : settings?.manager_numbers);
    if (managerNum) {
      const waLink = generateWhatsAppLink(managerNum, message);
      window.open(waLink, "_blank");
    }

    toast.success("Request received! Koko notified our team and someone will assist you shortly. 🐒", { duration: 5000 });
    
    // Reset form
    setSelectedCategory(null);
    setFormData({ ...formData, message: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCategory) {
      toast.error("Please select a category");
      return;
    }

    if (!formData.guest_name || !formData.room_number || !formData.whatsapp || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Save guest info immediately for future forms
    setGuestInfo({
      name: formData.guest_name,
      room: formData.room_number,
      whatsapp: formData.whatsapp,
    });

    setSubmitting(true);
    try {
      // 1. Check Verification Status
      const status = await getGuestOTPStatus(formData.whatsapp);
      
      if (status.is_verified) {
        // Already verified today, proceed directly
        await finalizeSubmit();
      } else {
        // Need verification, send OTP
        const sendResult = await sendGuestOTP(formData.whatsapp, propertyId);
        if (sendResult.success) {
          setShowOTPDialog(true);
          toast.success(sendResult.message || "Verification code sent!");
        } else {
          toast.error(sendResult.error || "Failed to send verification code. Please try again.");
        }
      }
    } catch (error) {
      console.error("Verification check failed:", error);
      const backendError = error.response?.data?.detail;
        const errorMsg = Array.isArray(backendError) ? backendError[0] : backendError;
        toast.error(
          errorMsg || "We're experiencing a technical issue. Please contact the manager at reception for assistance. 🙏",
          { duration: 8000 }
        );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyAndSubmit = async (e) => {
    e.preventDefault();
    if (otpValue.length < 4) {
      toast.error("Please enter a valid 4-digit code");
      return;
    }

    setVerifying(true);
    try {
      const result = await verifyGuestOTP(formData.whatsapp, otpValue);
      if (result.success) {
        toast.success("Verified!");
        setShowOTPDialog(false);
        await finalizeSubmit();
      } else {
        toast.error("Invalid code. Please try again.");
      }
    } catch (error) {
      console.error("Verification failed:", error);
      const backendError = error.response?.data?.detail;
        const errorMsg = Array.isArray(backendError) ? backendError[0] : backendError;
        toast.error(
          errorMsg || "We're experiencing a technical issue. Please contact the manager at reception for assistance. 🙏",
          { duration: 8000 }
        );
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    setResending(true);
    try {
      const sendResult = await sendGuestOTP(formData.whatsapp, propertyId);
      toast.success(sendResult.message || "New code sent!");
    } finally {
      setResending(false);
    }
  };

  const handleCallOTP = async () => {
    setCalling(true);
    try {
      const sendResult = await sendGuestOTP(formData.whatsapp, propertyId, "call");
      toast.success(sendResult.message || "Calling your phone now!");
    } catch (error) {
      console.error("Failed to request voice OTP:", error);
      toast.error("Failed to place call. Please try again.");
    } finally {
      setCalling(false);
    }
  };

  return (
    <GuestLayout>
      <div className="min-h-screen" data-testid="requests-page">
        {/* Header */}
        <div className="page-header bg-white border-b border-[#E8E4D9]">
          <button
            onClick={() => navigate(`/${getPropertySlug()}`)}
            className="flex items-center gap-2 text-[#6B705C] mb-4 hover:text-[#2A9D8F] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 data-testid="page-title">Requests</h1>
          <p>Tell Koko what you need — from room cleaning to fresh towels</p>
        </div>

        {/* Categories */}
        <div className="p-6">
          <p className="text-[#6B705C] mb-4">Select a category:</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex flex-col items-center gap-3 p-4 rounded-2xl border-2 transition-all duration-200 ${
                  selectedCategory === category.id
                    ? "border-[#2A9D8F] bg-[#2A9D8F]/5"
                    : "border-[#E8E4D9] bg-white hover:border-[#FFC107]"
                }`}
                data-testid={`category-${category.id}`}
              >
                <div className={`w-12 h-12 rounded-xl ${category.color} flex items-center justify-center`}>
                  <category.icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-[#264653]">{category.label}</span>
              </button>
            ))}
          </div>

          {/* Request Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-6 border border-[#E8E4D9]/50" data-testid="request-form">
            <h3 className="font-serif text-xl text-[#264653] mb-6">Your Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="guest_name" className="text-[#264653]">Your Name *</Label>
                <Input
                  id="guest_name"
                  value={formData.guest_name}
                  onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                  className="hm-input mt-1"
                  placeholder="John Doe"
                  required
                  data-testid="request-input-guest-name"
                />
              </div>

              <div>
                <Label htmlFor="room_number" className="text-[#264653]">Room Number *</Label>
                <Input
                  id="room_number"
                  value={formData.room_number}
                  onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                  className="hm-input mt-1"
                  placeholder="101"
                  required
                  data-testid="request-input-room-number"
                />
              </div>
            </div>

            <div className="mb-4">
              <Label htmlFor="whatsapp" className="text-[#264653]">WhatsApp Number *</Label>
              <Input
                id="whatsapp"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                className="hm-input mt-1"
                placeholder="+91 98765 43210"
                required
                data-testid="request-input-whatsapp"
              />
            </div>

            <div className="mb-6">
              <Label htmlFor="message" className="text-[#264653]">How can we help? *</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="hm-input mt-1 min-h-[120px]"
                placeholder="Describe your request in detail..."
                required
                data-testid="request-input-message"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary flex items-center justify-center gap-2"
              disabled={submitting || !selectedCategory}
              data-testid="submit-request"
            >
              <Send className="w-5 h-5" />
              {submitting ? "Sending..." : "Send Request"}
            </Button>
          </form>

          {/* Urgent Support */}
          <div className="mt-8 p-6 bg-[#FFC107]/10 rounded-3xl border border-[#FFC107]/20 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#FFC107] flex items-center justify-center shrink-0">
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h4 className="font-bold text-[#264653] mb-1">Urgent Issue?</h4>
              <p className="text-[#6B705C] text-sm">
                If you have an urgent issue, please call our manager directly at{" "}
                <a href={`tel:${settings?.staff2_whatsapp || ""}`} className="font-bold text-[#264653] hover:underline">
                  {settings?.staff2_whatsapp || "the front desk"}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* OTP Verification Dialog */}
      <Dialog open={showOTPDialog} onOpenChange={setShowOTPDialog}>
        <DialogContent className="sm:max-w-md bg-white rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#264653]">
              Verify Your Request
            </DialogTitle>
            <DialogDescription className="text-[#6B705C]">
              We've sent a 4-digit code to {formData.whatsapp}. This helps us prevent unsolicited submissions.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleVerifyAndSubmit} className="space-y-6 mt-4">
            <div className="flex justify-center">
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, ""))}
                className="hm-input text-center text-3xl tracking-[0.5em] font-bold h-16 w-52"
                placeholder="••••••"
                required
                autoFocus
              />
            </div>

            <div className="text-center flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={resending || calling}
                className="text-[#2A9D8F] text-sm font-medium hover:underline disabled:opacity-50"
              >
                {resending ? "Sending..." : "Didn't get the code? Resend"}
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={handleCallOTP}
                disabled={resending || calling}
                className="text-[#2A9D8F] text-sm font-medium hover:underline disabled:opacity-50"
              >
                {calling ? "Calling..." : "Call me instead"}
              </button>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary h-12 text-lg"
              disabled={verifying}
            >
              {verifying ? "Verifying..." : "Verify & Send Request"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </GuestLayout>
  );
}
